import Component from '@glimmer/component';
import { t } from 'ember-intl';
import TioWordmark from './tio-wordmark';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

const ZENDESK_HELP_CENTER = 'https://tio.zendesk.com/hc/en-us';
const PRIVACY_POLICY = 'https://www.tuition.io/privacy-policy';
const TERMS_OF_SERVICE = 'https://www.tuition.io/terms-of-service';

export interface BetaSiteFooterSignature {
  Args: {
    /**
     * Co-brand key, written to `data-beta-brand` so the stylesheet can re-tint
     * the wash and the wordmark. Omit for Tuition.io native, which is correct
     * for every partner without a bespoke brand. tio-employee passes its
     * `beta-brand` service; tio-admin has no co-branding and passes nothing.
     */
    brand?: string;
    /**
     * The partner's configurable line, which note 2 moves here from the header
     * banner ("giving this configurable line of copy more room"). In
     * tio-employee that is the company's `programTitle`. Omitted entirely when
     * absent — the row disappears rather than showing placeholder copy.
     */
    tagline?: string;
  };
  Element: HTMLElement;
}

/**
 * Brand Updates 2026 site footer.
 *
 * Source: Figma "Brand Updates 2026" → Side Navigation & Footer, "New Footer
 * Component" (node-id 4-8542 light, 17-3089 dark).
 *
 * Lives in tio-common because both employee and admin render it. It takes no
 * app services so it can mount in either; anything app-specific arrives as an
 * argument.
 *
 * The footer's four annotations all move an element here from somewhere else:
 *
 * 1. the Tuition.io wordmark, out of the side nav
 * 2. the partner tagline, out of the header banner
 * 3. the CourseCa$h legal string
 * 4. Support and Help, which "may also be shown in the resources section of the
 *    side navigation" — so the rail keeping them is intended, not duplication
 *
 * Plus nav note 5: Privacy Policy and Terms of Service leave the rail because
 * they link out to marketing pages. That makes this their only home, so the
 * Legal column is load-bearing.
 */
export default class BetaSiteFooter extends Component<BetaSiteFooterSignature> {
  @service declare intl: IntlService;

  get copyright(): string {
    return this.intl.t('site_footer.copyright', { year: new Date().getFullYear() });
  }

  /**
   * The disclosure interpolates the canonical CourseCa$h mark rather than
   * retyping it, so the mark's `$` and ™ styling stays in one place.
   *
   * Both calls need `htmlSafe`: the outer one so the sentence's own `<sup>` for
   * the ® renders, and the inner one so the mark arrives as a SafeString instead
   * of being escaped into visible tags.
   */
  get courseCashDisclosure() {
    return this.intl.t('site_footer.course_cash_disclosure', {
      courseCash: this.intl.t('common.tas.course_cash.default', { htmlSafe: true }),
      htmlSafe: true,
    });
  }

  <template>
    <footer
      class="tio-beta-footer"
      data-beta-brand={{@brand}}
      data-test-beta-site-footer
      ...attributes
    >
      <div class="tio-beta-footer__top">
        <div class="tio-beta-footer__brand">
          <div class="tio-beta-footer__powered">
            <span>{{t "site_footer.powered_by"}}</span>
            <TioWordmark class="tio-beta-footer__wordmark" />
          </div>

          {{#if @tagline}}
            <p class="tio-beta-footer__tagline">{{@tagline}}</p>
          {{/if}}

          <p class="tio-beta-footer__copyright">{{this.copyright}}</p>
        </div>

        <div class="tio-beta-footer__cols">
          <div class="tio-beta-footer__col">
            <div class="tio-beta-footer__col-title">{{t "site_footer.contact"}}</div>
            <div class="tio-beta-footer__links">
              <a
                href={{ZENDESK_HELP_CENTER}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-beta-footer__link"
                data-test-beta-site-footer-support
              >{{t "sidebar.support"}}</a>
              <a
                href={{ZENDESK_HELP_CENTER}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-beta-footer__link"
                data-test-beta-site-footer-help
              >{{t "sidebar.help"}}</a>
            </div>
          </div>

          <div class="tio-beta-footer__col">
            <div class="tio-beta-footer__col-title">{{t "site_footer.legal"}}</div>
            <div class="tio-beta-footer__links">
              <a
                href={{PRIVACY_POLICY}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-beta-footer__link"
                data-test-beta-site-footer-privacy
              >{{t "sidebar.privacy_policy"}}</a>
              <a
                href={{TERMS_OF_SERVICE}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-beta-footer__link"
                data-test-beta-site-footer-terms
              >{{t "sidebar.terms_of_service"}}</a>
            </div>
          </div>
        </div>
      </div>

      <div class="tio-beta-footer__fine">{{this.courseCashDisclosure}}</div>
    </footer>
  </template>
}
